import React, { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, Paper } from "@mui/material";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import { usePagination } from "Components/uiControls/Table/hooks";
import {
  searchHistoryIncidents,
  sendGenericExport,
  openHistoryOverViewIncident,
  clearRedux,
} from "Redux/actions";
import { Table, SearchInput, Toolbar, Button } from "Components/uiControls";
import { FilterButton } from "Components/Table";
import { TableButtons } from "Components/uiControls/Table";
import moment from "moment";
import { PillTime } from "./pillTime";
import { GetFormatForMoment, formatTimeHour } from "Core/data/Helpers";
import { permissionUnit } from 'Modules/incidents/.globals/utils'
import { getMethodToFormatDuration } from "Modules/reports/views/main/components/reportsTab/helper";
import { MenuComponent } from "Components/Menu";
import { Tooltip } from "@gpstrackit/gtc-react-controls";
import Delete from "../delete";
import Reactivate from "../reactivate";
import { NotificationManager } from "Components/Notifications";
import useExport from "Components/Table/Export";
import { getUnit } from "../../../../.globals/utils";

export const HistoryTable = ({ setShowSidePanel, showSidePanel, date }) => {
  const tableRef = useRef();
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();

  const INIT_FILTER = {
    conditions: [
      {
        fields: "isDeleted",
        terms: ["0"],
        exact: true,
      },
      {
        fields: "ended",
        terms: ["1"],
        exact: true,
      },
      {
        fields: "name",
        terms: [""],
        exact: false,
      },
      {
        range: {
          GTE: moment().startOf("day"),
          LTE: moment().subtract(7, "d").startOf("day"),
        },
      }
    ],
    sort: [
      {
        field: "id",
        order: "DESC",
      },
    ],
    limit: 10,
    offset: 0,
  };

  const [filters, setFilters] = useState();
  const [searchText, onSearchText] = useState("");
  const [openReactivate, setOpenReactivate] = useState(false);
  const [selectedReactivate, setSelectedReactivate] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filterUnitsData, setFilterUnitsData] = useState([]);
  const pagination = usePagination(tableRef, searchHistoryIncidents, filters);
  const {
    historyIncidents: data,
    loadingHistoryIncidents: loading,
    totalHistoryIncidents: total,
  } = useSelector((state) => state.historyIncidentsRedux);
  const { user } = useSelector((state) => state.securityRedux);
  const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const exportResult = useSelector((state) => state.commomRedux.exportResult);

  const { dateformat, durationFormat } = user;
  const formatDate = GetFormatForMoment(dateformat);

  useEffect(() => {
    if(!filters) return
    dispatch(searchHistoryIncidents(filters));
  }, [filters]);

  useEffect(() => {
    if (exportResult) {
      NotificationManager.success(
        messages["exportInProcessBody"],
        messages["success"],
        4000,
        null,
        null,
        "info",
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null }));
    }
  }, [exportResult]);

  useEffect(() => {
    if (!date) return;
    const handler = setTimeout(() => {
      setFilters({
        conditions: [
          {
            fields: "isDeleted",
            terms: ["0"],
            exact: true,
          },
          {
            fields: "ended",
            terms: ["1"],
            exact: true,
          },
          {
            fields: "name",
            terms: [searchText],
            exact: false,
          },
          {
            range: {
              GTE: date?.end,
              LTE: date?.start,
            },
          },
        ],
        sort: [
          {
            field: "id",
            order: "DESC",
          },
        ],
        limit: 10,
        offset: 0,
      });
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [date, searchText]);

  useEffect(() => {
    tableRef?.current?.setPage(1);
    tableRef?.current?.setRowsPerPage(10);
    pagination.setTotal(total || 0);
  }, [total]);

  useEffect(() => {
    if (data?.length > 0) {
      const filterData = permissionUnit(data, units);
      if (filterData?.length > 0) {
        setFilterUnitsData(filterData);
      }
    }
  }, [data, loading]);

  //change locales
  useEffect(() => {
    if (data?.length) {
      pagination.setColumns([
        {
          key: "name",
          show: true,
          title: messages["name_incident_history"],
          export: true,
          render: (item) => {
            const unitId = item?.unitId;
            const duration = moment(moment(item?.end)).diff(
              moment(item?.start),
              "s"
            );
            const durationFormatTime =
              getMethodToFormatDuration(durationFormat);
            const durationResult = duration
              ? durationFormatTime(duration, messages)
              : "0 min, 0 sec";
            const unit = getUnit(unitId, units);
            let truncateLabel = item?.name;
            if (truncateLabel?.length > 20) {
              truncateLabel = (
                <Tooltip title={truncateLabel}>
                  {truncateLabel.slice(0, 20) + "..."}
                </Tooltip>
              );
            }

            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                      onClick={() => {
                        dispatch(
                          openHistoryOverViewIncident({
                            data: {
                              incident: {
                                ...item,
                                duration: durationResult,
                                reportedBy: item?.incidentLog?.reportedBy,
                                endedBy: item?.incidentLog?.endedBy,
                              },
                              reading: item?.reading,
                              unit,
                            },
                            open: true,
                          })
                        );
                      }}
                      noWrap
                    >
                      {truncateLabel}
                    </Typography>
                    <MenuComponent
                      disabled={loading}
                      options={[
                        messages["reactive_incident_history"],
                        messages["delete_incident_history"],
                      ]}
                      icon={"more_vert"}
                      className="context-menu"
                      onClick={(type, value) => actionMenuItem(type, value)}
                      value={item}
                    />
                  </div>
                </Grid>
              </Grid>
            );
          },
          functionString: "return row.name;",
        },
        {
          key: "vehicle",
          show: true,
          title: messages["vehicle_incident_history"],
          export: true,
          render: (item) => {
            let truncateLabel = item?.label;
            if (truncateLabel?.length > 20) {
              truncateLabel = (
                <Tooltip title={truncateLabel}>
                  {truncateLabel.slice(0, 20) + "..."}
                </Tooltip>
              );
            }

            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{truncateLabel}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: "return row.label;",
        },
        {
          key: "start",
          show: true,
          title: messages["stared_incident_history"],
          export: true,
          render: (item) => {
            const start = moment(item?.start).format(
              `${formatDate} hh:mm:ss a`
            );
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{start}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: `return moment(row.start).format(${formatDate} hh:mm:ss a)`,
        },
        {
          key: "end",
          show: true,
          title: messages["ended_incident_history"],
          export: true,
          render: (item) => {
            const end = moment(item?.end).format(`${formatDate} hh:mm:ss a`);
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{end}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: `return moment(row.end).format(${formatDate} hh:mm:ss a)`,
        },
        {
          key: "duration",
          show: true,
          title: messages["duration_incident_history"],
          export: false,
          render: (item) => {
            const duration = moment(moment(item?.end)).diff(
              moment(item?.start),
              "s"
            );
            const durationFormatTime =
              getMethodToFormatDuration(durationFormat);
            const durationResult = duration
              ? durationFormatTime(duration, messages)
              : "0 min, 0 sec";
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{durationResult}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString:
            "return moment(moment(row?.end)).diff(moment(row?.start), 's')",
        },
        {
          key: "reportedBy",
          show: true,
          title: messages["reportedBy_incident_history"],
          export: true,
          render: (item) => {
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>
                    {item?.incidentLog?.reportedBy}
                  </Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: "return row.?incidentLog?.reportedBy",
        },
        {
          key: "endedBy",
          show: true,
          title: messages["endedBy_incident_history"],
          export: true,
          render: (item) => {
            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{item?.incidentLog?.endedBy}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: "return row.?incidentLog?.endedBy",
        },
        {
          key: "note",
          show: true,
          title: messages["note_incident_history"],
          export: true,
          render: (item) => {
            let truncateLabel = item?.note;
            if (truncateLabel?.length > 30) {
              truncateLabel = (
                <Tooltip title={truncateLabel}>
                  {truncateLabel.slice(0, 30) + "..."}
                </Tooltip>
              );
            }

            return (
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className="context-menu-container"
              >
                <Grid item xs zeroMinWidth>
                  <Typography noWrap>{truncateLabel}</Typography>
                </Grid>
              </Grid>
            );
          },
          functionString: "return row.note;",
        },
      ]);
    }
  }, [data, locale, loading]);

  const actionMenuItem = (type, value) => {
    switch (type) {
      case "Reactivate Incident":
      case "Reactivar Incidente":
        setSelectedReactivate(value);
        setOpenReactivate(true);
        break;
      case "Delete Incident":
      case "Eliminar Incidente":
        setSelectedItems([value]);
        setOpenDelete(true);
        break;
    }
  };

  const getHeaders = () => {
    return [
      messages["name_incident_history"],
      messages["vehicle_incident_history"],
      messages["stared_incident_history"],
      messages["ended_incident_history"],
      messages["duration_incident_history"],
      messages["reportedBy_incident_history"],
      messages["endedBy_incident_history"],
      messages["note_incident_history"],
    ];
  };

  const getDataToExport = (rows) => {
    let data = [];

    rows.forEach((row) => {
      const getDuration = moment(moment(row?.end)).diff(
        moment(row?.start),
        "s"
      );
      const durationFormatTime = getMethodToFormatDuration(durationFormat);
      const durationResult = getDuration
        ? durationFormatTime(getDuration, messages)
        : "0 min, 0 sec";

      const name = row?.name || "";
      const vehicle = row?.label || "";
      const start = moment(row?.start).format(`${formatDate} hh:mm:ss a`) || "";
      const end = moment(row?.end).format(`${formatDate} hh:mm:ss a`) || "";
      const duration = durationResult || "";
      const reportedBy = row?.incidentLog?.reportedBy || "";
      const endedBy = row?.incidentLog?.endedBy || "";
      const note = row?.note || "";

      data.push([
        name,
        vehicle,
        start,
        end,
        duration,
        reportedBy,
        endedBy,
        note,
      ]);
    });

    return data;
  };

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    pagination?.selectedItems || [],
    null,
    `Incidents-${moment().unix()}`,
    "landscape",
    true
  );

  return (
    <Grid item container>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <div style={{ display: "flex" }}>
          <Grid item>
            <FilterButton
              tooltip={messages["alertsTabView_FilterLabel"]}
              onClick={() => setShowSidePanel(!showSidePanel)}
            />
          </Grid>
          <Grid item>
            <Button
              type="icon"
              icon="delete"
              placement="bottom"
              tooltip={messages["delete"]}
              disabled={pagination?.selectedItems?.length == 0}
              onClick={(e) => {
                setSelectedItems(pagination?.selectedItems);
                setOpenDelete(true);
              }}
            />
          </Grid>
          <Grid item>
            <TableButtons
              messages={messages}
              tableRef={tableRef}
              disabled={pagination?.selectedItems?.length === 0}
              buttons={["export"]}
              onExport={(type) => {
                if (pagination?.selectedItems?.length > 0) {
                  if (type === "Csv") {
                    return exportCSVPdf.exportCSV();
                  } else if (type === "Pdf") {
                    return exportCSVPdf.exportPDF();
                  }
                }
                const columns = tableRef.current?.columns;
                const headers =
                  columns
                    .filter((x) => x.show && x.export)
                    .map((x) => {
                      return {
                        header: x.title,
                        column: x.key,
                        functionString: x.functionString,
                      };
                }) || [];

                const tableHeader = [
                    {
                      name: messages["name_incident_history"],
                      value: "",
                    },
                    {
                      name: messages["vehicle_incident_history"],
                      value: "",
                    },
                    {
                      name: messages["stared_incident_history"],
                      value: "",
                    },
                    {
                      name: messages["ended_incident_history"],
                      value: ""
                    },
                    {
                      name: messages["duration_incident_history"],
                      value: "",
                    },
                    {
                      name: messages["reportedBy_incident_history"],
                      value: ""
                    },
                    {
                        name: messages["endedBy_incident_history"],
                        value: ""
                    },
                    {
                        name: messages["note_incident_history"],
                        value: ""
                    },

                  ];
                
                dispatch(
                  sendGenericExport(
                    "Incidents History",
                    type,
                    `
                    query searchIncidents ($conditions: [IncidentSearchInput], $sort: [IncidentSortInput], $limit: Int, $offset: Int) {
                        searchIncidents (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                            items{
                                id
                                unitId
                                name
                                start
                                end
                                shortcode
                                note
                                label
                                incidentLog{
                                    reportedBy
                                    endedBy
                                }
                            }
                            total
                        }
                    }`,
                    {
                      ...filters,
                    },
                    headers,
                    {
                        unit: "pt",
                        size: "A4",
                        orientation: "landscape",
                        fontSize: 10,
                    },
                      tableHeader,
                      null,
                      null,
                    { 6: { cellWidth: 140 } }
                  )
                );
              }}
            />
          </Grid>
        </div>
        <Grid item>
          <PillTime
            date={date}
            setShowSidePanel={setShowSidePanel}
            showSidePanel={showSidePanel}
          />
        </Grid>
        <Grid item xs={12} sm={4} className="mb-10 text-right">
          <SearchInput
            placeholder={messages["search"]}
            disabled={loading}
            searchText={searchText}
            onSearchText={(e) => onSearchText(e)}
          />
        </Grid>
      </Grid>

      {!loading && data?.length === 0 ? (
        <div
          style={{
            height: `calc(100vh - 200px)`,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {messages["noInformation_incident_history"]}
        </div>
      ) : (
        <Table
          ref={tableRef}
          keyColumn="id"
          loading={loading}
          messageNoData={true}
          topheight={190}
          data={data}
          total={total}
          hidePagination={false}
          {...pagination}
        />
      )}

      <Delete
        open={openDelete}
        setOpen={setOpenDelete}
        listDelete={selectedItems}
        filters={filters}
        pagination={pagination}
        tableRef={tableRef}
        setSelectedItems={setSelectedItems}
      />

      <Reactivate
        open={openReactivate}
        setOpen={setOpenReactivate}
        incident={selectedReactivate}
        filters={filters}
      />
    </Grid>
  );
};
